import ExpiresAfter, {expiresAfterName} from "domain/ExpiresAfter";
import {UploadAbortedError} from "core/upload/uploadContract";
import {UploadInterruptedError, UploadPausedError, UploadSessionGoneError} from "core/upload/uploadFailures";
import {sessionExpireAfter, type SessionDelivery, type SessionDisplay, type UploadSessionRecord} from "core/upload/resume/uploadSession";
import type {DiscardOutcome} from "core/upload/resume/keptUploads";
import type {TransferPayload} from "../components/useCreateForm";

/**
 * Where the composer goes when a hosted upload this browser keeps stops, and what it keeps of a send
 * (kitecorp/transferify-ui#3204). React-free, so the composer only applies what these answer.
 */

/** Why the "Continue your upload" card is shown again with a sentence of its own, beside the summary. */
export type ResumeProblem =
	/** The connection stayed lost for the whole retry window; the upload paused in this page. */
	| {kind: "interrupted"}
	/** The files picked again are not the ones the upload declared. */
	| {kind: "mismatch"; firstMissing: string; missingCount: number}
	/**
	 * The upload could not go on, though nothing about it is wrong: storage lost one of its uploads, the api kept
	 * failing to start a file (kitecorp/transferify-ui#3589), or continuing it failed. The session is still kept,
	 * so the sender tries again or discards it.
	 */
	| {kind: "failed"}
	/** Another tab or window is uploading it right now. */
	| {kind: "elsewhere"}
	/** Discarding it did not reach the api; it is still kept, so it can be discarded again. */
	| {kind: "discardFailed"};

/** Where an upload attempt that failed leads. */
export type KeptUploadEnd =
	/** Cancelled on the progress card, which voided the transfer: back to composing. */
	| {kind: "cancelled"}
	/** Its transfer no longer exists: said plainly. */
	| {kind: "gone"}
	/**
	 * Still kept: the card again, saying why. `pageFilesUsable` is false when the files this page holds can no
	 * longer be read, so the card falls back on the browser's copy or asks for them again.
	 */
	| {kind: "kept"; problem: ResumeProblem | null; pageFilesUsable: boolean}
	/** Over: the ordinary failed-send screen. */
	| {kind: "failed"};

/**
 * Where a failed hosted upload leads, a new send or a continued one alike.
 *
 * @param stillKept       whether the browser still keeps the upload, read after it failed: the upload client keeps
 *                        a session only for a stop a continue can get past, so this is what tells the two apart
 * @param deliveryPending whether every file is stored and only notifying the recipients failed. The delivery retry
 *                        owns that: the parked delivery keeps the session, and its lock, until it is retried or
 *                        discarded, so the card would offer an upload this very page holds.
 */
export function keptUploadEnd(error: unknown, {stillKept, deliveryPending}: {
	stillKept: boolean;
	deliveryPending: boolean;
}): KeptUploadEnd {
	if (deliveryPending) {
		return {kind: "failed"};
	}
	if (error instanceof UploadAbortedError) {
		return {kind: "cancelled"};
	}
	if (error instanceof UploadSessionGoneError) {
		return {kind: "gone"};
	}
	if (!stillKept) {
		return {kind: "failed"};
	}
	if (error instanceof UploadInterruptedError) {
		return {kind: "kept", problem: {kind: "interrupted"}, pageFilesUsable: true};
	}
	if (error instanceof UploadPausedError && error.reason === "unreadable") {
		// No sentence of its own: the card asks for the files again unless the browser kept a copy to use.
		return {kind: "kept", problem: null, pageFilesUsable: false};
	}
	return {kind: "kept", problem: {kind: "failed"}, pageFilesUsable: true};
}

/**
 * What the composer says once a discard went through: that it was discarded, that its transfer was gone already,
 * or nothing, back to composing, for one that had finished after all or that another page had already ended.
 *
 * @param outcome what the discard came to, or `notKept` when the upload was no longer kept to claim
 */
export function afterDiscard(outcome: Exclude<DiscardOutcome, "kept"> | "notKept"): "discarded" | "gone" | "compose" {
	return outcome === "discarded" || outcome === "gone" ? outcome : "compose";
}

/**
 * The retention a continued upload states (its success card, its failed-send screen): the session's own, or
 * three days, the default, when this build no longer knows the name it was kept under.
 */
export function continuedRetention(record: UploadSessionRecord): ExpiresAfter {
	return sessionExpireAfter(record) ?? ExpiresAfter.THREE_DAYS;
}

/**
 * What a kept upload must remember of a send that only the composer knows: who it goes to, with the note they
 * read, and what its success card says once a later page finishes it.
 */
export function keptSendOf(payload: TransferPayload, note: string, initialRecipientEmails: string[] | undefined): {
	delivery: SessionDelivery;
	display: SessionDisplay;
} {
	return {
		delivery: payload.delivery === "recipients"
			? {
				transferType: "recipients",
				toEmails: payload.recipientSelection.emails,
				groupIds: payload.recipientSelection.groups.map(group => group.id),
				message: note,
			}
			: {transferType: "link", toEmails: [], groupIds: [], message: note},
		display: {
			expireAfter: expiresAfterName(payload.expireAfter),
			passwordProtected: Boolean(payload.password),
			...(payload.maxDownloads === undefined ? {} : {maxDownloads: payload.maxDownloads}),
			...(initialRecipientEmails === undefined ? {} : {initialRecipientEmails}),
		},
	};
}
